/**
 * Theming — brand assets, typography, and route-class palettes.
 *
 * **Public** (marketing + matching POC): dark purple + pink + orange — see
 * `globals.css` `[data-app-theme="public"]`.
 *
 * **Auth (guest)** — login / reset / accept-invite under `(auth-public)`:
 * warm stone + teal — `authPublicPalette` + `[data-app-theme="auth"]` (overrides
 * marketing defaults on `:root`).
 *
 * **Private default** (authenticated admin before shelter branding): light lavender
 * canvas + white cards + magenta primary + orange secondary — mirrored in
 * `[data-app-theme="private"]` (dashboard reference UI). Phase 5 merges API
 * branding onto this subtree only.
 *
 * Runtime: `applyThemeFromConfig()` sets **font** tokens on `:root` only; color
 * tokens come from scoped CSS so layouts are not overwritten after hydration.
 */

/** Marketing & `(public)` routes — purple + pink + orange (reference UI) */
export const publicPalette = {
  background: '#2D2138',
  foreground: '#ffffff',
  muted: '#3d3248',
  mutedForeground: '#c9c2d4',
  border: '#5a4d6b',
  card: '#453A56',
  cardForeground: '#ffffff',
  primaryForeground: '#ffffff',
  accent: '#FFF5E6',
  accentForeground: '#211A21',
  destructive: '#dc2626',
  destructiveForeground: '#ffffff',
  /** Sticky header / light chrome on public routes */
  header: '#ffffff',
  headerForeground: '#211A21',
  headerMuted: '#666666',
  /** Secondary CTA: outline “Back”, “Top match” badge */
  highlight: '#FF8C42',
  highlightForeground: '#ffffff',
  primary: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#E62B8D',
    700: '#c0266e',
    800: '#a21c5d',
    900: '#86194f',
    950: '#510d30',
  },
} as const;

/** Guest auth routes `(auth-public)` — stone + teal (distinct from marketing shell) */
export const authPublicPalette = {
  background: '#fafaf9',
  foreground: '#0c0a09',
  muted: '#78716c',
  mutedForeground: '#57534e',
  border: '#e7e5e4',
  card: '#ffffff',
  cardForeground: '#0c0a09',
  primaryForeground: '#ffffff',
  accent: '#f5f5f4',
  accentForeground: '#1c1917',
  destructive: '#dc2626',
  destructiveForeground: '#ffffff',
  header: '#ffffff',
  headerForeground: '#0c0a09',
  headerMuted: '#78716c',
  highlight: '#0d9488',
  highlightForeground: '#ffffff',
  primary: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e',
  },
} as const;

/**
 * Authenticated `/admin` — aligned with `src/app/ds-authenticated.css` (Figma DS,
 * light-mode tokens → `--theme-*`).
 */
export const privateDefaultPalette = {
  background: '#ffffff',
  foreground: '#1c1526',
  muted: '#f7f5fa',
  mutedForeground: '#4a3f5c',
  border: '#eeeeee',
  card: '#ffffff',
  cardForeground: '#1c1526',
  primaryForeground: '#ffffff',
  accent: '#fce4f1',
  accentForeground: '#1c1526',
  destructive: '#dd3730',
  destructiveForeground: '#ffffff',
  success: '#17a83d',
  successForeground: '#ffffff',
  popover: 'rgba(58, 42, 74, 0.95)',
  popoverForeground: '#ffffff',
  header: '#f7f5fa',
  headerForeground: '#1c1526',
  headerMuted: '#4a3f5c',
  highlight: '#f47521',
  highlightForeground: '#ffffff',
  primary: {
    50: '#fce4f1',
    100: '#f8c7e0',
    200: '#f2aed0',
    300: '#e14c9d',
    400: '#e858a5',
    500: '#ed2991',
    600: '#ed2991',
    700: '#8e1156',
    800: '#751247',
    900: '#5c0e38',
    950: '#3a0a24',
  },
} as const;

export const themeConfig = {
  brand: {
    shortName: 'Friends for Life',
    legalName: 'Friends for Life',
  },
  logos: {
    icon: '/brand/logo-icon.svg',
    wordmark: '/brand/logo-wordmark.svg',
    /** Horizontal preferred lockup for public branding nav (Figma). */
    brandingHorizontal: '/brand/friendr-logo.svg',
    /** FRIENDR badge for public “Powered by” footer (Figma). */
    poweredByFriendr: '/brand/powered-by-friendr.png',
  },
  fonts: {
    sans: '"Inter", ui-sans-serif, system-ui, sans-serif',
    heading: '"Inter", ui-sans-serif, system-ui, sans-serif',
  },
  publicPalette,
  authPublicPalette,
  privateDefaultPalette,
} as const;

export type ThemeConfig = typeof themeConfig;
export type PublicPalette = typeof publicPalette;
export type AuthPublicPalette = typeof authPublicPalette;
export type PrivateDefaultPalette = typeof privateDefaultPalette;
